/**
 * What the firm decided about its own addresses (#420), as the desk needs to know: whether a
 * dispatcher may place one, and how a building's entrances are offered unless the dispatcher
 * chose otherwise.
 *
 * Read once from `/api/tenant/current` and kept for the session, as the pre-bookings switch
 * is: a firm's setting, changed on a settings page and not between two calls. The server
 * refuses a dispatcher's place when the switch is off whatever this says.
 */

import { useEffect, useState } from 'react';

import { request, type Schema } from '@taxi/api-client';

export type PlacesFeatures = Schema<'PlacesFeatures'>;

/** What the desk assumes until the answer arrives, and when it cannot be had. */
const ASSUMED: PlacesFeatures = { dispatchersPlace: false, entrancesAfterAddress: false };

let asked: Promise<PlacesFeatures> | null = null;

function ask(): Promise<PlacesFeatures> {
  asked ??= request<Pick<Schema<'CurrentTenant'>, 'places'>>('/api/tenant/current').then(
    (firm) => firm.places ?? ASSUMED,
    () => ASSUMED,
  );

  return asked;
}

/** Forgets the answer, for the settings page after it changes it. */
export function forgetPlacesFeatures(): void {
  asked = null;
}

export function usePlacesFeatures(): PlacesFeatures {
  const [features, setFeatures] = useState(ASSUMED);

  useEffect(() => {
    let current = true;

    void ask().then((answer) => {
      if (current) setFeatures(answer);
    });

    return () => {
      current = false;
    };
  }, []);

  return features;
}
